/**
 * CreatorHelperTarifeTab · iter142.1 Nacharbeit
 *
 * Als TAB in /studio/helper eingebunden (nicht als separate Route).
 * P4: Free = eigene Features + Limits; Standard = "Alles aus Free +"; Premium = "Alles aus Standard +"
 * P5: Alle 3 Karten identisch gestylt (kein Standard-Highlight)
 * P6: Checkout-Error sichtbar wenn data.url fehlt
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, CheckCircle2, Loader2, Info, Clock, Ban, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import PaymentChoiceModal from "@/components/PaymentChoiceModal";

const FEATURE_ROWS = [
  { key: "text_gen_marketplace", label: "Marketplace-Textgenerator (neutral)" },
  { key: "text_gen_stx", label: "stilltouchedX-Textgenerator (sinnlich)" },
  { key: "text_gen_social", label: "Social-Content-Generator" },
  { key: "text_gen_bio", label: "Bio-/Profil-Optimierung" },
  { key: "text_gen_tags", label: "Tag- & Keyword-Vorschläge" },
  { key: "profile_audit", label: "Profil-Audit + Score" },
  { key: "content_score", label: "Content-Score" },
  { key: "content_score_trend", label: "Score-Trend über Zeit" },
  { key: "coaching_wizard", label: "Coaching-Wizard" },
  { key: "image_analysis", label: "Bildanalyse (Belichtung/Kontrast/Schärfe)" },
  { key: "image_optimize", label: "Technische Bildoptimierung" },
  { key: "image_face_blur", label: "Gesichtsverfremdung (Bild)" },
  { key: "image_tattoo_removal", label: "Tattoo-Entfernung" },
  { key: "image_mole_removal", label: "Muttermal-Entfernung" },
  { key: "image_mark_removal", label: "Sonstige Merkmal-Entfernung" },
  { key: "price_suggestions", label: "Preisempfehlungen an Listings" },
  { key: "listing_analytics", label: "Listing-Analytics (Funnel)" },
  { key: "watch_analytics_basic", label: "Basis Watch-Analytics" },
  { key: "watch_analytics_full", label: "Vollständige Watch-Analytics (Retention/Heatmap)" },
  { key: "benchmark_extended", label: "Erweiterte Kategorie-Benchmarks" },
];

// Quota-Limit je Feature — angezeigt hinter dem Label wenn Feature aktiv ist.
const FEATURE_LIMITS = {
  text_gen_marketplace: (q) => q.text_gen_monthly === -1 ? "unbegrenzt" : `bis ${q.text_gen_monthly}/Monat`,
  text_gen_stx: (q) => q.text_gen_monthly === -1 ? "unbegrenzt" : `bis ${q.text_gen_monthly}/Monat`,
  text_gen_social: (q) => q.text_gen_monthly === -1 ? "unbegrenzt" : `bis ${q.text_gen_monthly}/Monat`,
  text_gen_bio: (q) => q.text_gen_monthly === -1 ? "unbegrenzt" : `aus ${q.text_gen_monthly}er-Textbudget`,
  text_gen_tags: (q) => q.text_gen_monthly === -1 ? "unbegrenzt" : `aus ${q.text_gen_monthly}er-Textbudget`,
  image_face_blur: (q) => q.image_face_blur_monthly === -1 ? "unbegrenzt" : `${q.image_face_blur_monthly}/Monat`,
  image_analysis: (q) => q.image_ops_monthly === -1 ? "unbegrenzt" : `aus ${q.image_ops_monthly} Bild-Ops/Monat`,
  image_optimize: (q) => q.image_ops_monthly === -1 ? "unbegrenzt" : `aus ${q.image_ops_monthly} Bild-Ops/Monat`,
  image_tattoo_removal: (q) => q.image_ops_monthly === -1 ? "unbegrenzt" : `aus ${q.image_ops_monthly} Bild-Ops/Monat`,
  image_mole_removal: (q) => q.image_ops_monthly === -1 ? "unbegrenzt" : `aus ${q.image_ops_monthly} Bild-Ops/Monat`,
  image_mark_removal: (q) => q.image_ops_monthly === -1 ? "unbegrenzt" : `aus ${q.image_ops_monthly} Bild-Ops/Monat`,
  video_face_blur: (q) => q.video_ops_monthly === -1 ? "unbegrenzt" : `${q.video_ops_monthly}/Monat`,
  video_analysis: (q) => q.video_ops_monthly === -1 ? "unbegrenzt" : `${q.video_ops_monthly}/Monat`,
  video_optimize: (q) => q.video_ops_monthly === -1 ? "unbegrenzt" : `${q.video_ops_monthly}/Monat`,
};

function labelWithLimit(row, cfg) {
  const fn = FEATURE_LIMITS[row.key];
  const lim = fn ? fn(cfg.quotas || {}) : null;
  return lim ? `${row.label} (${lim})` : row.label;
}

function ownFeatures(cfg) {
  return FEATURE_ROWS.filter((r) => cfg?.features?.[r.key]);
}

// P4: Diff = Features im höheren Tarif, die im niedrigeren fehlen
function diffFeatures(higher, lower) {
  return FEATURE_ROWS.filter(
    (r) => higher?.features?.[r.key] && !lower?.features?.[r.key],
  );
}

// P4: Für jeden Tarif eine Feature-Liste im Stil "Alles aus X plus …" aufbauen
function buildTierFeatureList(cfg, allConfigs) {
  const code = cfg.plan_code;
  if (code === "FREE") {
    return { intro: null, items: ownFeatures(cfg).map((r) => labelWithLimit(r, cfg)) };
  }
  if (code === "STANDARD") {
    const free = allConfigs.find((c) => c.plan_code === "FREE");
    return {
      intro: "Alles aus Free +",
      items: diffFeatures(cfg, free).map((r) => labelWithLimit(r, cfg)),
    };
  }
  if (code === "PREMIUM") {
    // Premium = alle Standard-Features, aber praktisch unbegrenzt (Fair-Use).
    // Wir listen konkret, WOFÜR das gilt (statt nur "Fair-Use").
    return {
      intro: "Alles aus Standard – Fair-Use (praktisch unbegrenzt) für:",
      items: ownFeatures(cfg).map((r) => labelWithLimit(r, cfg)),
    };
  }
  return { intro: null, items: ownFeatures(cfg).map((r) => labelWithLimit(r, cfg)) };
}

export default function CreatorHelperTarifeTab() {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [billing, setBilling] = useState("MONTHLY");
  const [pendingPlan, setPendingPlan] = useState(null); // {code, amount}
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get("/creator-helper/config"),
        api.get("/creator-helper/me"),
      ]);
      setConfigs(c.data?.plans || []);
      setState(s.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startTrial = async () => {
    setBusy("trial");
    setError(null);
    try {
      await api.post("/creator-helper/trial", {
        plan_code: state?.trial_plan_code || "STANDARD",
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Trial konnte nicht gestartet werden");
    } finally {
      setBusy(null);
    }
  };

  // P6 · Checkout-Fix: bei fehlendem data.url sichtbaren Error zeigen
  const buy = async (planCode, method) => {
    setBusy(`buy-${planCode}`);
    setError(null);
    try {
      const { data } = await api.post("/creator-helper/checkout", {
        plan_code: planCode,
        billing_period: billing,
        payment_method: method,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      setError({
        kind: "error",
        text:
          "Checkout konnte nicht gestartet werden (keine Zahlungs-URL vom Server). " +
          "Bitte andere Zahlungsart wählen oder Support kontaktieren.",
      });
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "PAYPAL_COMING_SOON") {
        setError({ kind: "warning", text: "PayPal-Abos werden gerade fertig gestellt — bitte Stripe wählen." });
      } else if (d?.code === "ALREADY_ON_PLAN") {
        setError({ kind: "info", text: "Du bist bereits auf diesem Tarif." });
      } else {
        setError({ kind: "error", text: typeof d === "string" ? d : d?.message || "Buchung fehlgeschlagen. Bitte erneut versuchen." });
      }
    } finally {
      setBusy(null);
      setPendingPlan(null);
    }
  };

  const cancelEot = async () => {
    if (!window.confirm(t("creator_helper.cancel_period_end_confirm"))) return;
    setBusy("cancel");
    try {
      await api.post("/creator-helper/cancel-eot");
      await load();
    } catch (e) {
      setError({ kind: "error", text: e?.response?.data?.detail || "Kündigung fehlgeschlagen" });
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div data-testid="helper-tarife-loading" className="flex items-center justify-center py-16">
        <Loader2 size={22} className="animate-spin text-[#999]" />
      </div>
    );
  }

  const currentPlan = state?.plan?.plan_code || "FREE";
  const currentPlanLabel = state?.plan?.label || currentPlan;

  return (
    <div data-testid="helper-tarife-tab" className="space-y-6">
      {/* Status-Karte */}
      <div className="rounded-2xl border border-[#E5E5E5] p-4 bg-[#FAFAFA]">
        <p className="text-xs font-bold uppercase tracking-wider text-[#666] mb-2">{t("creator_helper.your_status")}</p>
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-2xl font-black">{currentPlanLabel}</span>
          {state?.plan?.is_trial && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">
              <Clock size={10} /> Testphase
            </span>
          )}
          {state?.plan?.source === "ADMIN_GRANT" && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">
              Admin-Freischaltung
            </span>
          )}
        </div>
        {state?.plan?.expires_at && (
          <p className="text-xs text-[#666] mt-1">
            Läuft bis: {new Date(state.plan.expires_at).toLocaleDateString("de-DE")}
          </p>
        )}
        {state?.plan?.status === "PAST_DUE" && (
          <div data-testid="helper-reactivate-banner" className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3">
            <p className="text-xs font-semibold text-amber-900">{t("tarife.paused_payment_failed")}</p>
            <button
              data-testid="helper-reactivate-btn"
              onClick={() => {
                const c = configs.find((x) => x.plan_code === currentPlan);
                setPendingPlan({ code: currentPlan, amount: Number((billing === "MONTHLY" ? c?.price_monthly_eur : c?.price_yearly_eur) || 0), label: c?.label || currentPlan });
              }}
              className="mt-2 h-9 px-4 rounded-full bg-[#0A0A0A] text-white text-xs font-bold hover:bg-[#222] inline-flex items-center gap-2"
            >
              <Sparkles size={12} /> Erneut aktivieren
            </button>
          </div>
        )}
        {currentPlan !== "FREE" && !state?.plan?.is_trial && state?.plan?.source !== "ADMIN_GRANT" && (
          <button
            data-testid="helper-cancel-eot"
            onClick={cancelEot}
            disabled={busy === "cancel"}
            className="mt-3 h-9 px-4 rounded-full border border-[#E5E5E5] text-xs font-semibold text-[#666] hover:bg-white disabled:opacity-40 flex items-center gap-2"
          >
            {busy === "cancel" ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
            Zum Periodenende kündigen
          </button>
        )}
        {state?.trial_eligible && currentPlan === "FREE" && (
          <button
            data-testid="helper-start-trial"
            onClick={startTrial}
            disabled={busy === "trial"}
            className="mt-3 h-10 px-5 rounded-full bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-2"
          >
            {busy === "trial" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Kostenlose Testphase starten ({state.trial_days} Tage {state.trial_plan_code})
          </button>
        )}
      </div>

      {/* Quota-Übersicht */}
      {state?.usage?.buckets && (
        <div data-testid="helper-usage" className="rounded-2xl border border-[#E5E5E5] p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-[#666] mb-3">
            Aktueller Verbrauch ({state.usage.month})
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(state.usage.buckets).map(([k, v]) => (
              <div key={k} data-testid={`helper-usage-${k}`} className="rounded-xl bg-[#FAFAFA] p-2">
                <p className="text-[10px] uppercase tracking-wider text-[#999]">{k.replace(/_/g, " ")}</p>
                <p className="text-sm font-bold">
                  {v.used} / {v.unlimited ? "∞" : v.limit}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Billing-Auswahl (Zahlart wird erst beim Freischalten gewählt) */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="inline-flex rounded-full border border-[#E5E5E5] p-0.5" role="group">
          {["MONTHLY", "YEARLY"].map((b) => (
            <button
              key={b}
              data-testid={`helper-billing-${b.toLowerCase()}`}
              onClick={() => setBilling(b)}
              className={`h-8 px-4 rounded-full text-xs font-bold transition-colors ${
                billing === b ? "bg-[#0A0A0A] text-white" : "text-[#666]"
              }`}
            >
              {b === "MONTHLY" ? "Monatlich" : "Jährlich (−17 %)"}
            </button>
          ))}
        </div>
      </div>

      {/* P5 · Tarif-Karten – ALLE 3 IDENTISCH GESTYLT */}
      <div className="grid md:grid-cols-3 gap-3">
        {configs.map((cfg) => {
          const isCurrent = cfg.plan_code === currentPlan;
          const monthly = cfg.price_monthly_eur;
          const yearly = cfg.price_yearly_eur;
          const price = billing === "MONTHLY" ? monthly : yearly;
          const isFree = cfg.plan_code === "FREE";
          const { intro, items } = buildTierFeatureList(cfg, configs);
          return (
            <div
              key={cfg.plan_code}
              data-testid={`helper-plan-${cfg.plan_code.toLowerCase()}`}
              // P5: Keine Sonder-Hervorhebung mehr für Standard – gleiche Border/Padding/Shadow
              className="rounded-2xl border border-[#E5E5E5] p-4 bg-white flex flex-col"
            >
              <div className="flex items-start justify-between mb-2">
                <p className="text-lg font-black">{cfg.label || cfg.plan_code}</p>
                {isCurrent && (
                  <span
                    data-testid={`helper-plan-current-${cfg.plan_code.toLowerCase()}`}
                    className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 border border-emerald-200 inline-flex items-center gap-1"
                  >
                    <CheckCircle2 size={10} /> Aktiv
                  </span>
                )}
              </div>
              <p className="text-3xl font-black">
                {isFree ? "0" : Number(price || 0).toFixed(2)} €
                <span className="text-xs font-normal text-[#666] ml-1">
                  {billing === "MONTHLY" ? "/ Monat" : "/ Jahr"}
                </span>
              </p>
              {billing === "YEARLY" && !isFree && monthly > 0 && (
                <p className="text-[10px] text-[#666] mt-1">
                  entspricht {(yearly / 12).toFixed(2)} € / Monat
                </p>
              )}

              {/* P4 · Feature-Liste im Stil "Alles aus … plus …" */}
              <div className="mt-3 flex-1">
                {intro && (
                  <p
                    data-testid={`helper-plan-intro-${cfg.plan_code.toLowerCase()}`}
                    className="text-xs font-bold text-[#0A0A0A] mb-1.5"
                  >
                    {intro}
                  </p>
                )}
                <ul className="space-y-1 text-xs text-[#0A0A0A]">
                  {items.map((label, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <CheckCircle2 size={12} className="text-emerald-600 mt-0.5 shrink-0" />
                      <span>{label}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {!isFree && !isCurrent && (
                <button
                  data-testid={`helper-buy-${cfg.plan_code.toLowerCase()}`}
                  onClick={() => setPendingPlan({ code: cfg.plan_code, amount: Number(price || 0), label: cfg.label })}
                  disabled={!!busy}
                  className="mt-4 w-full h-10 rounded-full bg-[#0A0A0A] text-white text-xs font-bold hover:bg-[#222] disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {busy === `buy-${cfg.plan_code}` ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Sparkles size={12} />
                  )}
                  {cfg.label} freischalten
                </button>
              )}
              {isFree && !isCurrent && (
                <p className="mt-4 text-[10px] text-[#999] text-center">{t("creator_helper.standard_plan_no_payment")}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Vollständiger Vergleich (Details) */}
      <details className="rounded-2xl border border-[#E5E5E5]">
        <summary
          data-testid="helper-feature-compare"
          className="p-3 cursor-pointer text-xs font-bold uppercase tracking-wider text-[#666]"
        >
          Vollständiger Funktionsvergleich
        </summary>
        <div className="overflow-x-auto p-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#E5E5E5]">
                <th className="text-left py-2 font-medium text-[#666]">Funktion</th>
                {configs.map((c) => (
                  <th key={c.plan_code} className="py-2 font-bold text-center">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_ROWS.map((r) => (
                <tr key={r.key} className="border-b border-[#F5F5F5]">
                  <td className="py-1.5 text-[#0A0A0A]">{r.label}</td>
                  {configs.map((c) => (
                    <td key={c.plan_code} className="py-1.5 text-center">
                      {c.features?.[r.key] ? (
                        <CheckCircle2 size={12} className="text-emerald-600 inline" />
                      ) : (
                        <span className="text-[#DDD]">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {/* P6 · Fehler/Info-Anzeige mit klarer Sichtbarkeit */}
      {error && (
        <div
          data-testid="helper-error"
          className={`rounded-2xl border p-3 text-xs flex items-start gap-2 ${
            error.kind === "info"
              ? "border-blue-200 bg-blue-50 text-blue-800"
              : error.kind === "warning"
                ? "border-amber-200 bg-amber-50 text-amber-800"
                : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          {error.kind === "error" ? (
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          ) : (
            <Info size={14} className="mt-0.5 shrink-0" />
          )}
          <span>{typeof error === "string" ? error : error.text}</span>
        </div>
      )}

      <PaymentChoiceModal
        open={!!pendingPlan}
        title={pendingPlan ? `${pendingPlan.label} freischalten (${billing === "MONTHLY" ? "monatlich" : "jährlich"})` : ""}
        amount={pendingPlan?.amount}
        allowedMethods={["STRIPE", "PAYPAL"]}
        notice={billing === "MONTHLY"
          ? "Dieses Abo verlängert sich automatisch alle 30 Tage. Jederzeit unter „Meine Käufe“ / im Studio kündbar – läuft dann bis zum Ende der bezahlten Laufzeit. Zahlung nur per Stripe oder PayPal."
          : "Dieses Abo verlängert sich automatisch jedes Jahr. Jederzeit unter „Meine Käufe“ / im Studio kündbar – läuft dann bis zum Ende der bezahlten Laufzeit. Zahlung nur per Stripe oder PayPal."}
        onClose={() => setPendingPlan(null)}
        onConfirm={(method) => buy(pendingPlan.code, method)}
      />
    </div>
  );
}
